import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Save, Loader2, RefreshCw, Download,
  ArrowLeft, Lock, Unlock, ShieldCheck,
} from 'lucide-react';
import IncomeSections from './tax-return/IncomeSections';
import TaxCalculationSidebar from './tax-return/TaxCalculationSidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TAX_YEARS = ['2026/27', '2025/26', '2024/25', '2023/24'];

const STATUS_COLORS = {
  draft: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  review: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  final: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  signed_off: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
};

const AccountantTaxReturn = () => {
  const { clientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [taxYear, setTaxYear] = useState(searchParams.get('tax_year') || TAX_YEARS[0]);
  const [data, setData] = useState(null);
  const [calc, setCalc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [signOffAmount, setSignOffAmount] = useState('');
  const [signingOff, setSigningOff] = useState(false);
  const [reopening, setReopening] = useState(false);

  const fetchReturn = useCallback(async (yr) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/accountant/tax-return/${clientId}?tax_year=${yr}`);
      setData(res.data);
      if (res.data.calculation_result) setCalc(res.data.calculation_result);
      if (res.data.actual_refund_received != null) setSignOffAmount(res.data.actual_refund_received.toString());
      else setSignOffAmount('');
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load tax return');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchReturn(taxYear);
  }, [taxYear, fetchReturn]);

  const handleYearChange = (yr) => {
    setTaxYear(yr);
    setSearchParams({ tax_year: yr });
  };

  const updateField = (field, value) => {
    if (data?.signed_off) return;
    setData(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const runCalculation = async () => {
    setCalculating(true);
    try {
      const res = await axios.get(`${API_URL}/api/accountant/tax-return/${clientId}/calculate?tax_year=${taxYear}`);
      setCalc(res.data);
    } catch {
      toast.error('Calculation failed');
    } finally {
      setCalculating(false);
    }
  };

  const saveReturn = async () => {
    setSaving(true);
    try {
      // eslint-disable-next-line no-unused-vars
      const { id, client_user_id, tax_year, created_at, updated_at, last_calculated,
              calculation_result, client_name, client_email, qualifying_days, trips_count,
              ...fields } = data;
      await axios.put(`${API_URL}/api/accountant/tax-return/${clientId}?tax_year=${taxYear}`, fields);
      toast.success('Tax return saved');
      setDirty(false);
      await runCalculation();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const refreshAppData = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API_URL}/api/accountant/tax-return/${clientId}/refresh?tax_year=${taxYear}`);
      toast.success(`Employment data refreshed: £${res.data.employment_income?.toLocaleString()}`);
      fetchReturn(taxYear);
    } catch {
      toast.error('Failed to refresh');
    } finally {
      setRefreshing(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/api/accountant/tax-return/${clientId}/pdf?tax_year=${taxYear}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SA100_${taxYear.replace('/', '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch {
      toast.error('PDF generation failed');
    } finally {
      setDownloading(false);
    }
  };

  // Other income helpers
  const addOtherIncome = () => {
    const items = [...(data.other_income || []), { label: '', amount: 0 }];
    updateField('other_income', items);
  };
  const updateOtherIncome = (idx, field, value) => {
    const items = [...(data.other_income || [])];
    items[idx] = { ...items[idx], [field]: value };
    updateField('other_income', items);
  };
  const removeOtherIncome = (idx) => {
    const items = (data.other_income || []).filter((_, i) => i !== idx);
    updateField('other_income', items);
  };

  const signOff = async () => {
    const amount = parseFloat(signOffAmount);
    if (isNaN(amount)) {
      toast.error('Please enter the actual refund amount received');
      return;
    }
    setSigningOff(true);
    try {
      await axios.post(`${API_URL}/api/accountant/tax-return/${clientId}/sign-off?tax_year=${taxYear}`, {
        actual_refund_received: amount,
      });
      toast.success('Tax return signed off successfully');
      fetchReturn(taxYear);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sign off');
    } finally {
      setSigningOff(false);
    }
  };

  const reopenReturn = async () => {
    setReopening(true);
    try {
      await axios.post(`${API_URL}/api/accountant/tax-return/${clientId}/reopen?tax_year=${taxYear}`);
      toast.success('Tax return reopened for amendments');
      setSignOffAmount('');
      fetchReturn(taxYear);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reopen');
    } finally {
      setReopening(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-sky-400" />
      </div>
    );
  }

  if (!data) return null;

  const isLocked = data.signed_off === true;

  return (
    <div className="max-w-5xl mx-auto space-y-5" data-testid="tax-return-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Button variant="ghost" size="sm" onClick={() => window.history.back()}
              className="h-8 px-2 text-slate-400 hover:text-white" data-testid="tax-return-back">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-xl font-bold text-white">HMRC Self-Assessment</h1>
            <Badge className={`${STATUS_COLORS[isLocked ? 'signed_off' : data.status]} text-[10px]`} data-testid="tax-return-status">
              {isLocked ? 'SIGNED OFF' : data.status?.toUpperCase()}
              {isLocked && <Lock className="h-2.5 w-2.5 ml-0.5" />}
            </Badge>
          </div>
          <p className="text-slate-400 text-sm ml-10">
            {data.client_name || 'Client'} — SA100 Tax Return
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={taxYear} onChange={e => handleYearChange(e.target.value)}
            className="bg-slate-800 border border-slate-600 text-white rounded-md h-9 text-sm px-3"
            data-testid="tax-year-select">
            {TAX_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={data.status || 'draft'} onChange={e => updateField('status', e.target.value)}
            disabled={isLocked}
            className="bg-slate-800 border border-slate-600 text-white rounded-md h-9 text-sm px-3 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="status-select">
            <option value="draft">Draft</option>
            <option value="review">Review</option>
            <option value="final">Final</option>
          </select>
          <Button variant="outline" size="sm" onClick={refreshAppData} disabled={refreshing}
            className="border-slate-600 text-slate-300 h-9" data-testid="refresh-app-data">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Button size="sm" onClick={saveReturn} disabled={saving || !dirty || isLocked}
            className="bg-sky-600 hover:bg-sky-700 h-9" data-testid="save-tax-return">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Save
          </Button>
          <Button size="sm" onClick={downloadPdf} disabled={downloading}
            className="bg-amber-600 hover:bg-amber-700 h-9" data-testid="download-pdf">
            {downloading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
            PDF
          </Button>
        </div>
      </div>

      {/* Signed-Off Banner */}
      {isLocked && (
        <Card className="bg-emerald-500/10 border-emerald-500/30" data-testid="signed-off-banner">
          <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-400" />
              <div>
                <p className="text-emerald-400 text-sm font-semibold">Tax Return Signed Off</p>
                <p className="text-slate-400 text-[10px]">
                  Actual refund: £{parseFloat(data.actual_refund_received || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                  {data.signed_off_at && ` — ${new Date(data.signed_off_at).toLocaleDateString('en-GB')}`}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={reopenReturn} disabled={reopening}
              className="border-amber-500/40 text-amber-400 hover:text-amber-300 h-8 text-xs" data-testid="reopen-tax-return">
              {reopening ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Unlock className="h-3 w-3 mr-1" />}
              Reopen for Amendments
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left column: income / SA sections */}
        <div className={`lg:col-span-2 space-y-4 ${isLocked ? 'opacity-60 pointer-events-none select-none' : ''}`}>
          <IncomeSections
            data={data}
            updateField={updateField}
            updateOtherIncome={updateOtherIncome}
            addOtherIncome={addOtherIncome}
            removeOtherIncome={removeOtherIncome}
          />
        </div>

        {/* Right column: tax calculation + quick actions + sign-off */}
        <TaxCalculationSidebar
          data={data}
          calc={calc}
          calculating={calculating}
          saving={saving}
          refreshing={refreshing}
          downloading={downloading}
          signOffAmount={signOffAmount}
          setSignOffAmount={setSignOffAmount}
          signingOff={signingOff}
          reopening={reopening}
          isLocked={isLocked}
          saveReturn={saveReturn}
          refreshAppData={refreshAppData}
          downloadPdf={downloadPdf}
          signOff={signOff}
          reopenReturn={reopenReturn}
        />
      </div>
    </div>
  );
};

export default AccountantTaxReturn;
